import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import { Users, Heart, Trophy, Lightbulb, ArrowRight } from "lucide-react";
import teamBuildingImage from "@/assets/team-building.jpg";
import photo1 from "@/assets/20260618_184839.jpg.asset.json";
import photo2 from "@/assets/20260618_163844.jpg.asset.json";
import photo3 from "@/assets/20260618_184758_EDITED.jpg.asset.json";
import photo4 from "@/assets/20260618_185405.jpg.asset.json";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/i18n/translations";

const heroSlides = [
  {
    src: teamBuildingImage,
    alt: "Groep collega's tijdens VR teambuilding event met escape room challenge - bedrijfsuitje activiteit",
  },
  {
    src: photo1.url,
    alt: "Team op de knieën met VR headsets tijdens een VR teambuilding sessie op locatie",
  },
  {
    src: photo2.url,
    alt: "Collega's spelen samen een VR game tijdens een bedrijfsuitje op kantoor",
  },
  {
    src: photo3.url,
    alt: "VR teambuilding met PlayfieldVR begeleiding en tablet met gameoverzicht",
  },
  {
    src: photo4.url,
    alt: "Groep met VR brillen in actie tijdens een VR groepsactiviteit op locatie",
  },
];

const Teambuilding = () => {
  const { language } = useLanguage();
  const t = translations[language].teambuilding;
  const [api, setApi] = useState<CarouselApi>();

  useEffect(() => {
    if (!api) return;
    const interval = setInterval(() => {
      api.scrollNext();
    }, 5000);
    return () => clearInterval(interval);
  }, [api]);

  
  const scrollToContact = () => {
    const element = document.getElementById("contact-form");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const benefits = [
    {
      icon: Users,
      title: t.benefits.collaboration.title,
      description: t.benefits.collaboration.description,
      gradient: "bg-transparent"
    },
    {
      icon: Heart,
      title: t.benefits.bonding.title,
      description: t.benefits.bonding.description,
      gradient: "bg-transparent"
    },
    {
      icon: Trophy,
      title: t.benefits.competition.title,
      description: t.benefits.competition.description,
      gradient: "bg-transparent"
    },
    {
      icon: Lightbulb,
      title: t.benefits.creativity.title,
      description: t.benefits.creativity.description,
      gradient: "bg-transparent"
    }
  ];

  return (
    <section id="teambuilding" className="py-14 sm:py-24 relative overflow-hidden scroll-mt-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-12 md:mb-16 animate-fade-in-up">
          <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-muted-foreground mb-5">
            {t.badge}
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight mb-5 px-4">
            {t.title} <span className="text-primary">{t.titleHighlight}</span>
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto px-4">
            {t.subtitle}
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid sm:grid-cols-2 gap-6 mb-10 sm:mb-16">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <Card
                key={index}
                className={`p-6 sm:p-8 ${benefit.gradient} border-border hover:border-primary/50 transition-colors duration-300 group animate-fade-in-up`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold mb-1 sm:mb-2">{benefit.title}</h3>
                    <p className="text-sm sm:text-base text-muted-foreground">{benefit.description}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Image and CTA Section */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="relative animate-fade-in-up order-2 lg:order-1">
            <Carousel
              setApi={setApi}
              opts={{ loop: true, align: "start" }}
              className="w-full"
            >
              <CarouselContent>
                {heroSlides.map((slide, index) => (
                  <CarouselItem key={index}>
                    <img
                      src={slide.src}
                      alt={slide.alt}
                      loading={index === 0 ? "eager" : "lazy"}
                      className="rounded-xl w-full aspect-[16/10] object-cover"
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="left-2 sm:left-4 bg-background/80 border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground" />
              <CarouselNext className="right-2 sm:right-4 bg-background/80 border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground" />
            </Carousel>
          </div>

          
          <div className="text-center lg:text-left animate-fade-in-up order-1 lg:order-2 px-4">
            <h3 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4">
              Maak Van Je Team<br />
              <span className="text-primary">Een VR-Team</span>
            </h3>
            <p className="text-base sm:text-lg text-muted-foreground mb-6 sm:mb-8">
              {language === 'nl' 
                ? "Onze VR-ervaringen zijn speciaal ontworpen om teams te versterken door unieke uitdagingen die alleen door samenwerking overwonnen kunnen worden."
                : "Our VR experiences are specially designed to strengthen teams through unique challenges that can only be overcome through collaboration."
              }
            </p>
            <Button
              size="lg"
              onClick={scrollToContact}
              className="bg-primary text-primary-foreground font-semibold text-base px-8 py-6 hover:bg-primary/90 transition-colors group"
            >
              {t.cta}
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Teambuilding;
